import axios from 'axios';
import { buildApiUrl } from '../../../config/api';
import authService from '../../authService';
import { ERROR_MESSAGES, IMPORT_SETTINGS } from '../config/qbConstants';
import { handleQBApiError } from '../api/errorHandler';

// Get all customers from QuickBooks via MySQL API
export const getAllCustomers = async (franchiseeId) => {
  try {
    const token = authService.getToken();
    if (!token) {
      throw new Error(ERROR_MESSAGES.USER_NOT_AUTHENTICATED);
    }
    
    const response = await axios.get(
      buildApiUrl(`quickbooks/customers/${franchiseeId}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data && response.data.customers) {
      return response.data.customers;
    } else {
      return [];
    }
  } catch (error) {
    handleQBApiError(error, 'customer fetch');
  }
};

// Get QuickBooks customer count
export const getQuickBooksCustomerCount = async (franchiseeId) => {
  try {
    const token = authService.getToken();
    if (!token) {
      throw new Error(ERROR_MESSAGES.USER_NOT_AUTHENTICATED);
    }
    
    const response = await axios.get(
      buildApiUrl(`quickbooks/customers/${franchiseeId}/count`),
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.totalCustomers || 0;
  } catch (error) {
    console.error('Error getting QB customer count:', error);
    return 0; // Fallback to 0 if can't get count
  }
};

// Import customers from QuickBooks to MySQL with chunked processing
export const importCustomers = async (franchiseeId, userId, progressCallback = null) => {
  try {
    const token = authService.getToken();
    if (!token) {
      throw new Error(ERROR_MESSAGES.USER_NOT_AUTHENTICATED);
    }
    
    console.log('Starting chunked QuickBooks customer import...');
    
    let startFrom = 0;
    let totalCustomers = 0;
    let totalImported = 0;
    let totalErrors = 0;
    let hasMore = true;
    const batchSize = IMPORT_SETTINGS.DEFAULT_BATCH_SIZE;

    while (hasMore) {
      console.log(`Processing batch starting from ${startFrom}...`);
      
      const response = await axios.post(
        buildApiUrl(`quickbooks/customers/${franchiseeId}/import/chunked`),
        { 
          batchSize,
          startFrom,
          totalCustomers: totalCustomers || null
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = response.data;
      
      // Update totals
      totalImported += result.batch.imported;
      totalErrors += result.batch.errors;
      totalCustomers = result.progress.total;
      hasMore = result.progress.hasMore;
      
      // Update progress
      if (progressCallback) {
        progressCallback(result.progress.current, totalCustomers, []);
      }
      
      console.log(`Batch complete: ${result.batch.imported} imported, ${result.batch.errors} errors. Progress: ${result.progress.current}/${totalCustomers}`);
      
      // Move to next batch
      startFrom = result.progress.current;
      
      // Small delay to prevent overwhelming the QB API
      if (hasMore) {
        await new Promise(resolve => setTimeout(resolve, IMPORT_SETTINGS.BATCH_DELAY_MS));
      }
    }
    
    console.log(`✅ Import completed: ${totalImported} imported, ${totalErrors} errors out of ${totalCustomers} total`);
    return { 
      imported: totalImported, 
      errors: totalErrors, 
      total: totalCustomers,
      details: { totalImported, totalErrors, totalCustomers }
    };

  } catch (error) {
    console.error('Error importing customers:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};